import { useReactiveVar } from '@apollo/client'
import classNames from 'classnames'

import { CLICK_CODE } from '@local/utils'

import FlickingArea from '@web/components/FlickingArea/FlickingArea'
import sendClickLog from '@web/libs/nlog/click/sendClickLog'
import { showDailyBriefingVar } from '@web/pages/v5/home/DailyBriefingSection/state'
import FlickingContentMoreButton from '@web/ui/components/FlickingContentMoreButton'
import { ListFilterWhiteButton } from '@web/ui/components/ListFilterButton'

import { Section, SectionHeader, SectionContent } from '../Section'
import BookingCardItem from './components/BookingCardItem'
import OrderCardItem from './components/OrderCardItem'
import TicketCardItem from './components/TicketCardItem'
import styles from './UpComingSection.module.scss'

type Coordinate = { latitude: number; longitude: number }

export type BookingCardItemType = {
  id: string
  type: 'booking'
  displayOrderTimestamp: string
  coordinate?: Coordinate
  placeId?: string
  bookingStatusCode?: string
  placeName?: string
} & React.ComponentProps<typeof BookingCardItem>['item']
export type OrderCardItemType = {
  id: string
  type: 'order'
  displayOrderTimestamp: string
  coordinate?: Coordinate
  placeId?: string
  orderedDateTimeText?: string
  bookingStatusCode?: string
  placeName?: string
} & React.ComponentProps<typeof OrderCardItem>['item']
export type TicketCardItemType = {
  id: string
  type: 'ticket'
  displayOrderTimestamp: string
  coordinate?: Coordinate
} & React.ComponentProps<typeof TicketCardItem>['item']

export type UpcomingSectionItemType = BookingCardItemType | OrderCardItemType | TicketCardItemType
export type MapUpcomingItemType = BookingCardItemType | OrderCardItemType
export type UpcomingSectionFilterType = {
  name: string
  count?: number
  onClick?: () => void
  isSelected?: boolean
  clickCodes?: {
    filterClick?: string
    getMoreClick?: string
  }
}

interface Props {
  upcomingItems: UpcomingSectionItemType[]
  upcomingFilters: UpcomingSectionFilterType[]
  totalUpcomingItemCount: number
  onClickMore?: ({ anchor }: { anchor?: boolean }) => void
  onClickTrainTicket?: () => void
  hasMore?: boolean
  title: string
}

export default function UIUpcomingSection({
  upcomingItems,
  upcomingFilters,
  totalUpcomingItemCount,
  onClickMore,
  hasMore = false,
  title,
}: Props) {
  const DISPLAY_UPCOMING_SECTION_MIN_COUNT = 3

  const isDailyBriefingExposed = useReactiveVar(showDailyBriefingVar)
  return (
    <Section
      noMargin
      className={classNames(styles.my_upcoming_section, { [styles.type_has_bg]: !isDailyBriefingExposed })}
    >
      <SectionHeader
        title={title}
        count={totalUpcomingItemCount}
        clickHandler={() => {
          sendClickLog({ area: CLICK_CODE['myupcoming.title'] })
          onClickMore?.({ anchor: false })
        }}
      />
      <SectionContent>
        {/* 필터 */}
        {upcomingFilters.length >= DISPLAY_UPCOMING_SECTION_MIN_COUNT && (
          <FlickingArea
            className={styles.filter_flicking_area}
            options={{
              align: 'prev',
            }}
          >
            {upcomingFilters.map(({ name, count, isSelected, onClick, clickCodes }: UpcomingSectionFilterType) => {
              return (
                <ListFilterWhiteButton
                  key={name}
                  name={name}
                  count={count}
                  selected={isSelected}
                  onClick={onClick}
                  clickCode={clickCodes?.filterClick}
                />
              )
            })}
          </FlickingArea>
        )}

        {/* 목록 */}
        <FlickingArea className={styles.card_flicking_area} options={{ cameraTag: 'ul', align: 'prev' }}>
          {upcomingItems.map((upcomingItem, idx) => {
            if (upcomingItem.type === 'order') {
              return (
                <OrderCardItem
                  key={upcomingItem.id}
                  item={upcomingItem}
                  clickCodes={{
                    cardClick: CLICK_CODE['myupcoming.card'],
                    refreshIcon: CLICK_CODE['myupcoming.cardrefresh'],
                    directionIcon: CLICK_CODE['myupcoming.directionicon'],
                  }}
                  cardIdx={idx}
                  isFlicking
                />
              )
            }

            if (upcomingItem.type === 'booking') {
              return (
                <BookingCardItem
                  key={upcomingItem.id}
                  item={upcomingItem}
                  clickCodes={{
                    cardClick: CLICK_CODE['myupcoming.card'],
                    bookingDetails: CLICK_CODE['myupcoming.bookingdetails'],
                    direction: CLICK_CODE['myupcoming.direction'],
                  }}
                  cardIdx={idx}
                  isFlicking
                />
              )
            }

            if (upcomingItem.type === 'ticket') {
              return (
                <TicketCardItem
                  key={upcomingItem.id}
                  item={upcomingItem}
                  clickCodes={{
                    cardClick: CLICK_CODE['myupcoming.card'],
                    bookingDetails: CLICK_CODE['myupcoming.bookingdetails'],
                  }}
                  cardIdx={idx}
                  isFlicking
                />
              )
            }

            return null
          })}
          {hasMore && (
            <li className={classNames(styles.list_item, styles.type_more, styles.type_flicking)}>
              <FlickingContentMoreButton
                onClick={() => onClickMore?.({ anchor: true })}
                clickCode={upcomingFilters.find(item => !!item.isSelected)?.clickCodes?.getMoreClick}
              />
            </li>
          )}
        </FlickingArea>
      </SectionContent>
    </Section>
  )
}
